// Global Instructions Rule Applied!
import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from '../../../state_manager/hooks/useAppDispatch'
import { useDrawer } from '../../../core/contexts/DrawerContext.jsx'
import { updateTask } from '../../../store/slices/tasksSlice'
// Optimistic updates removed - no longer supported
import { DealFields } from '../../../state_manager/data_models/deal_record.ts'
import { TaskRecordFields } from '../../../state_manager/data_models/task_record.ts'

/**
 * Custom hook for managing drawer state and operations for deal pipeline
 * Handles drawer opening, closing, and form submission logic specifically for pipeline deals
 * Uses pipeline deals state instead of filtered table deals state
 */
export const useDealPipelineDrawer = (pipelineDeals: any[]) => {
  const { isOpen: isDrawerOpen, recordId, isNew, openDrawerForNew, openDrawerForRecord, closeDrawer } = useDrawer()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  // Drawer handlers
  const handleOpenDrawerForNew = useCallback(() => {
    openDrawerForNew()
  }, [openDrawerForNew])

  const handleOpenDrawerForDeal = useCallback(
    (dealId: string) => {
      openDrawerForRecord(dealId)
    },
    [openDrawerForRecord]
  )

  // Handle deal name click (for navigation)
  const handleDealNameClick = useCallback(
    (dealId: string) => {
      handleOpenDrawerForDeal(dealId)
    },
    [handleOpenDrawerForDeal]
  )

  // Handle tasks click (for pipeline-specific navigation)
  const handleTasksClick = useCallback(
    (tasks: any[], dealRecord: any, taskId: string | null = null) => {
      // Extract deal ID from the deal record passed from the kanban card
      const dealId = dealRecord?.id

      if (dealId) {
        // Build query parameters
        const params = new URLSearchParams()

        // If specific task ID is provided, add it to open that task's detail panel
        if (taskId) {
          params.set('taskId', taskId)
        }

        // Open drawer with deal pipeline base path to stay in pipeline tab
        const queryString = params.toString()
        navigate(`/deals/pipeline/forms/drawer/${dealId}${queryString ? `?${queryString}` : ''}`)
      }
    },
    [navigate]
  )

  // Handle task toggle with optimistic updates
  const handleTaskToggle = useCallback(
    async (taskId: string, dealId: string, completed: boolean) => {
      try {
        // Optimistic update for UI responsiveness
        // Filter out completed tasks to match what the API returns (only incomplete tasks)
        const deal = pipelineDeals.find((d) => d[DealFields.deals__id] === dealId)
        const updatedTasks =
          deal?.tasks
            ?.map((task: any) => (task.id === taskId ? { ...task, completed } : task))
            .filter((task: any) => !task.completed) || []

        // Update task in backend - optimistic updates removed.
        // unwrap() throws on rejection, so a resolved result carries no error.
        await dispatch(
          updateTask({
            dealId,
            taskId,
            taskData: {
              [TaskRecordFields.tasks__id]: taskId,
              [TaskRecordFields.tasks__completed]: completed
            }
          })
        ).unwrap()
        // Note: Global task state is already updated by updateTask.fulfilled
      } catch (error) {
        console.error('Error updating task:', error)
      }
    },
    [dispatch, pipelineDeals]
  )

  // Handle drawer close
  const handleCloseDrawer = useCallback(() => {
    closeDrawer()
  }, [closeDrawer])

  return {
    // Drawer state
    isDrawerOpen,
    recordId,
    isNew,

    // Drawer handlers
    handleOpenDrawerForNew,
    handleOpenDrawerForDeal,
    handleDealNameClick,
    handleTasksClick,
    handleTaskToggle,
    closeDrawer: handleCloseDrawer
  }
}
